import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ProjectFileI, ProjectI } from 'src/app/models/project';
import { FolderI } from 'src/app/models/folder';
import { AllPublicationFolderE } from 'src/app/models/publication';
import { BulkAddFavoritesResultI } from 'src/app/models/favorites';

@Injectable({ providedIn: 'root' })
export class ProjectService {
    private serviceUrl = 'webfront/projects';

    constructor(private httpClient: HttpClient) {}

    getProjects(): Observable<ProjectI[]> {
        return this.httpClient.get<ProjectI[]>(this.serviceUrl);
    }

    getProject(id: number): Observable<ProjectI> {
        return this.httpClient.get<ProjectI>(`${this.serviceUrl}/${id}`);
    }

    getProjectFolders(id: number): Observable<FolderI[]> {
        return this.httpClient.get<FolderI[]>(`${this.serviceUrl}/${id}/folders`);
    }

    createProject(name: string): Observable<ProjectI> {
        return this.httpClient.post<ProjectI>(this.serviceUrl, { name });
    }

    renameProject(id: number, name: string): Observable<ProjectI> {
        return this.httpClient.put<ProjectI>(`${this.serviceUrl}/${id}`, { name });
    }

    deleteProject(id: number): Observable<unknown> {
        return this.httpClient.delete(`${this.serviceUrl}/${id}`);
    }

    getProjectPublications(projectId: number): Observable<ProjectFileI[]> {
        return this.httpClient.get<ProjectFileI[]>(`${this.serviceUrl}/${projectId}/publications`);
    }

    addPublicationToProject(
        projectId: number,
        publicationId: number,
        publicationType: AllPublicationFolderE
    ): Observable<ProjectFileI> {
        return this.httpClient.post<ProjectFileI>(`${this.serviceUrl}/${projectId}/publications`, {
            publicationId,
            publicationType
        });
    }

    addFavoritesFolderToProject(projectId: number, favoritesFolderId: number): Observable<BulkAddFavoritesResultI> {
        return this.httpClient.post<BulkAddFavoritesResultI>(`${this.serviceUrl}/${projectId}/favorites`, {
            favoritesFolderId
        });
    }

    removeProjectFile(id: number, publicationType: AllPublicationFolderE): Observable<void> {
        return this.httpClient.delete<void>(`${this.serviceUrl}/publications/${id}/${publicationType}`);
    }

    updateProjectFileDocumentVersion(id: number): Observable<ProjectFileI> {
        return this.httpClient.post<ProjectFileI>(`${this.serviceUrl}/publications/${id}/update-version`, {});
    }

    uploadPrivateFileToProject(
        projectId: number,
        payload: { file: File; title: string; consentDataProcessingAgreements: boolean }
    ): Observable<HttpEvent<unknown>> {
        const formData = new FormData();
        formData.append('file', payload.file);
        formData.append('fileName', payload.title);
        formData.append('consentDataProcessingAgreements', payload.consentDataProcessingAgreements ? '1' : '0');

        return this.httpClient.post(`${this.serviceUrl}/${projectId}/upload`, formData, {
            reportProgress: true,
            observe: 'events',
            headers: new HttpHeaders({ skipSpinner: 'true', skipTechnicalAndBusinessError: 'true' })
        });
    }
}
